import { APP_BASE_HREF, DatePipe } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, LOCALE_ID, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, TitleStrategy } from '@angular/router';
import { provideAccountWizardHintConfig } from '@datev/accountwizard-hintdialog';
import { provideAgreements } from '@datev/mydatev-agreements';
import { provideTracking } from '@datev/ngx-adobe-tracking';
import { provideDackl, withIcons } from '@datev/ngx-dackl';
import { provideAuth, withOptions, withReloginOverlay } from '@datev/ngx-dcal';
import { provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { AppStateService, PilotFeature } from 'commons';
import { environment } from '../../../environments/environment';
import { AppErrorHandler } from './app-error.handler';
import { routes } from './app-routes';
import { AppSuffixTitleStrategy } from './app-suffix-title-strategy';
import { DeliveryTypePipe } from '../shared/pipes/delivery-type.pipe';
import { firstValueFrom } from 'rxjs';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

const pilotFeatureMappings = [
  { uriExtension: 'pilot-redesign-home', pilotFeatures: [PilotFeature.REDESIGN_HOMEPAGE] }
];

/**
 * Bestimmt die korrekte Umgebungs-Stage für den AccountWizardHintDialog
 * basierend auf den bestehenden Environment-Konfigurationen.
 */
function getAccountWizardStage(): 'DEV' | 'QS' | 'PROD' {
  const stageSource = `${environment.entryPoint} ${window.location.origin}`.toLowerCase();

  if (stageSource.includes('localhost') || stageSource.includes('.dev.datev.de')) {
    return 'DEV';
  }

  if (stageSource.includes('.qs.datev.de')) {
    return 'QS';
  }

  return 'PROD';
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    importProvidersFrom(
      BrowserModule
    ),
    provideAppInitializer(() => {
        const initializerFn = (initializeTranslations)(inject(TranslateService));
        return initializerFn();
      }),
    DatePipe,
    DeliveryTypePipe,
    { provide: LOCALE_ID, useValue: 'de-DE' },
    { provide: TitleStrategy, useClass: AppSuffixTitleStrategy },
    {
      provide: APP_BASE_HREF,
      useFactory: getBaseHref,
      deps: [AppStateService]
    },
    { provide: ErrorHandler, useClass: AppErrorHandler },
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    provideTracking({
      adobeTrackingScriptUrl: environment.adobe_analytics_script_url,
      useManualPageViewTracking: true
    }),
    provideAgreements(),
    provideAccountWizardHintConfig({
      appName: 'DATEV MyUpdates',
      // Anwendung den Stages entsprechend konfigurieren:
      // DEV: 'DEV', QS: 'QS', PROD: 'PROD' (default)
      linkDestinationStage: getAccountWizardStage()
    }),
    provideAuth(
      withOptions({
        contextRoot: deriveBaseHref(document.baseURI, location.origin, location.href).url,
        inactivityTimeout: true,
        inactivityTimeoutDuration: environment.inactivityTimeout,
        disableSessionManagement: environment.disableSessionManagement
      }),
      // Wenn wir Produkte aufrufen ohne Login müssen wir die Methode context: unauthenticated() aufgerufen.
      // Diese Methode triggert den withReloginOverlay(). D. h. Seite geht auf und sofort eine Aufforderung für Login
      withReloginOverlay()
    ),
    provideAnimationsAsync(),
    provideDackl(withIcons()),
    provideRouter(routes),

    { provide: 'Window', useValue: window },
  ]
};

export function httpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.76.json');
}

export function initializeTranslations(translate: TranslateService): () => Promise<any> {
  return (): Promise<any> => {
    translate.setDefaultLang('de');
    return firstValueFrom(translate.use('de'));
  };
}

// *** Routinen für Pilot Flag Behandlung
export interface BaseHrefInfo {
  url: string;
  pilotFeatures: PilotFeature[];
}

export function getBaseHref(appState: AppStateService): string {
  const baseHrefInfo = deriveBaseHref(
    document.baseURI,
    location.origin,
    location.href
  );

  appState.activePilotFeatures = baseHrefInfo.pilotFeatures;

  return baseHrefInfo.url;
}

export function deriveBaseHref(documentBaseURI: string, locationOrigin: string, locationHref: string): BaseHrefInfo {
  let baseHref = documentBaseURI.slice(locationOrigin.length);
  const pathWithoutBaseUri = locationHref.slice(documentBaseURI.length);
  let pilotState: PilotFeature[] = [];

  for (const pilotLink of pilotFeatureMappings) {
    const pilotUriExtensionWithFinalSlash = pilotLink.uriExtension + '/';
    if (pathWithoutBaseUri.startsWith(pilotUriExtensionWithFinalSlash) ||
      pathWithoutBaseUri === pilotLink.uriExtension) {
      baseHref += pilotUriExtensionWithFinalSlash;
      pilotState = pilotLink.pilotFeatures;
      break;
    }
  }

  return {url: baseHref, pilotFeatures: pilotState};
}
